import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { notification } from 'ui-library';
import ServiceManager from 'services/ServiceManager';
import { useGetContactGroups } from 'hooks/rest/useGetContactGroups';
import { useCallback, useMemo } from 'react';

const useGetActive3bGroup = ({
    clientId, currentPortfolioId, currentContactGroupId, isJA,
}) => {
    const { i18n: { language }, t } = useTranslation();

    const { data: contactGroups } = useGetContactGroups({
        contactId: clientId,
        isEnabled: !!clientId && !!currentContactGroupId,
    });

    const contactGroupIds = useMemo(() => (isJA && currentContactGroupId ? [currentContactGroupId] : contactGroups?.map(group => group?.groupId)), [isJA, currentContactGroupId, contactGroups]);

    const fetchData = useCallback(async () => {
        if (contactGroupIds?.length) {
            try {
                return await Promise.allSettled(contactGroupIds?.map(contactGroupId => contactGroupId && ServiceManager.customCaseManagementServices('getActive3bPortfoliosByContactGroup', [{ contactGroupId, language }])));
            } catch (error: any) {
                notification.open({ content: `${t('contactGroups.somethingWentWrong')} ${error.message}`, type: 'error' });

                return null;
            }
        } else {
            return null;
        }
    }, [contactGroupIds, language]);

    const {
        data, error, isLoading, isFetching,
    } = useQuery({
        queryKey: ['fetchActivePortfolios', {
            language, currentContactGroupId, clientId, contactGroupIds,
        }],
        enabled: !!clientId && !!currentContactGroupId && (contactGroupIds?.length ?? 0) > 0,
        retry: false,
        queryFn: fetchData,
    });

    const portfolioData = useMemo(() => data?.flatMap((entry: any) => entry?.value?.data?.casePortfolioInfos)?.filter(({ portfolioId }: {portfolioId: number}) => portfolioId !== +currentPortfolioId) || [], [data, currentPortfolioId]);

    return {
        error, isLoading: isLoading || isFetching, data: portfolioData,
    };
};

export default useGetActive3bGroup;
